import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

// import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'

import { cn } from '@/lib/utils'

export type MaskTransitionHandler = {
  enterTimeline: () => Promise<void>
  leaveTimeline: () => Promise<void>
  resetTimeline: () => void
}

/* .to('[data-mask-transparent]', { yPercent: -100, duration: 1, ease: 'power3.out' }, 0)
.to('[data-mask-loader]', { yPercent: 100, duration: 1, ease: 'power3.out' }, 0) */

const MaskTransition = forwardRef<MaskTransitionHandler>((_, ref) => {
  const root = useRef<HTMLDivElement>(null)
  const [isAnimating, setIsAnimating] = useState(false)

  const { contextSafe } = useGSAP({ scope: root })

  const setIdleState = contextSafe(() => {
    gsap.set(root.current, {
      autoAlpha: 0
    })
  })

  useGSAP(
    () => {
      setIdleState()
    },
    { scope: root, dependencies: [setIdleState] }
  )

  const enterTimeline = contextSafe(() => {
    return new Promise<void>((resolve) => {
      setIsAnimating(true)
      gsap.timeline({ onComplete: resolve }).to(root.current, {
        autoAlpha: 1,
        duration: 0.5
      })
    })
  })

  const leaveTimeline = contextSafe(() => {
    return new Promise<void>((resolve) => {
      gsap
        .timeline({ onComplete: resolve })
        .to(root.current, {
          autoAlpha: 0,
          duration: 0.5
        })
        .call(() => {
          setIsAnimating(false)
        })
    })
  })

  const resetTimeline = contextSafe(() => {
    setIdleState()
  })

  useImperativeHandle(
    ref,
    () => ({
      leaveTimeline,
      enterTimeline,
      resetTimeline
    }),
    [enterTimeline, leaveTimeline, resetTimeline]
  )

  return (
    <div
      ref={root}
      data-mask-transition
      className={cn(
        'flex min-h-[100dvh] w-full flex-col overflow-hidden',
        'pointer-events-none fixed inset-0 z-transition',
        'bg-surface-primay-invert-dark',
        'flex items-center justify-center',
        'opacity-0',
        { 'pointer-events-auto': isAnimating }
      )}
    >
      <div
        role="status"
        aria-live="polite"
        className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-spacing-sm text-text-primary-dark"
      >
        <SVGIcon
          symbol="logo"
          className="h-[16px] w-[116px] fill-current"
          aria-hidden="true"
        />
        {/*  <Spinner
          variant="medium"
          className="size-[1.6rem] [&_circle]:stroke-[.8rem]"
        /> */}
        <span className="sr-only">Loading</span>
      </div>
    </div>
  )
})

export default MaskTransition
